import { useEffect, useRef, useState } from 'react'

import { esNativo, fotoNativa } from '@/modules/campo/camara'
import { calentarUbicacion } from '@/lib/nativo/ubicacion'
import { estamparFoto, posicionParaSello } from '@/modules/campo/estampar'
import { direccionDesdeCoords, precargarGeocodificador } from '@/lib/geocodificacion/google'
import { useSesion } from '@/modules/auth/useSesion'
import { huellaSha256 } from '@/lib/campo/hashFoto'
import { identificadorUnico } from '@/shared/utilidades/identificador'
import { useColaDeFotos } from '@/modules/campo/useColaDeFotos'
import { Aviso } from '@/shared/ui/Aviso/Aviso'
import { CamaraEnVivo } from '@/shared/ui/Camara/CamaraEnVivo'
import { fechaHora } from '@/shared/utilidades/formato'

import {
  useBorrarFoto,
  useFotosDeLaOrden,
  useUrlesDeFotos,
} from './useFotosOrden'
import estilos from './FotosOrden.module.css'

/** La X de quitar, como icono de trazo: el caracter «✕» se veia tosco y de
 *  distinto grosor en cada telefono. */
const IconoQuitar = (
  <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor"
       strokeWidth="2.4" strokeLinecap="round" aria-hidden="true">
    <path d="M6 6l12 12M18 6L6 18" />
  </svg>
)

/**
 * LAS FOTOS DE LA VISITA.
 *
 * Sirve en los dos momentos, y son distintos:
 *
 *   · AL CREAR LA ORDEN todavía no hay a qué colgarlas —la orden no existe—,
 *     así que se escogen y se quedan en la mano; el formulario las sube en
 *     cuanto la orden nace. Se muestran igual, porque quien acaba de escoger
 *     seis fotos necesita ver cuáles lleva.
 *   · EN LA FICHA ya hay orden: se suben al momento.
 *
 * El mismo componente para los dos, porque para quien lo usa es el mismo
 * gesto, y partirlo en dos habría dejado dos maneras distintas de mirar lo
 * mismo.
 */

type Props = {
  /** Null mientras la orden no existe: entonces solo se escogen. */
  ordenServicioId: string | null
  /** Las escogidas antes de que exista la orden. */
  pendientes?: File[]
  onPendientes?: (archivos: File[]) => void
  /** Sin esto solo se miran. */
  puedeEditar?: boolean
  /** Abre la cámara en cuanto monta el componente (primer uso, sin fotos). */
  autoAbrir?: boolean
  /** Para el sello: qué trabajo es y dónde. */
  sello?: { referencia?: string | null; direccion?: string | null }
}

/**
 * NO HAY SELECTOR DE ARCHIVOS, Y ES LA REGLA.
 *
 * Había un `<input type="file" capture>`, y `capture` es solo una SUGERENCIA:
 * en escritorio el navegador la ignora y abre el explorador; en muchos Android
 * abre la cámara pero con un botón para saltar a la galería. Mientras exista
 * ese input, la foto puede venir de otra obra y de otro día.
 *
 * Estas fotos sustentan una cotización y acaban en el acta que el cliente
 * firma. Se toman en vivo —cámara nativa en la app, `getUserMedia` en el
 * navegador— y se les quema el sello con fecha, hora y coordenadas.
 */

export function FotosOrden({
  ordenServicioId,
  pendientes = [],
  onPendientes,
  puedeEditar = true,
  autoAbrir = false,
  sello,
}: Props) {
  const { perfil } = useSesion()
  const [camara, setCamara] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Trabajando en una foto: desde que vuelve de la cámara hasta que termina de
  // sellarla y subirla. Es lo que enciende la rueda y bloquea un segundo toque.
  const [procesando, setProcesando] = useState(false)
  // La foto que se está mirando en grande. Null = visor cerrado.
  const [ampliada, setAmpliada] = useState<string | null>(null)
  const disparadoRef = useRef(false)

  const { data: fotos = [] } = useFotosDeLaOrden(ordenServicioId)
  const { data: urles = [] } = useUrlesDeFotos(fotos.map((f) => f.ruta))
  const borrar = useBorrarFoto()
  // Lo que espera subir en el teléfono, para esta orden.
  const cola = useColaDeFotos(ordenServicioId)

  /**
   * Las miniaturas de lo que todavía no se ha subido.
   *
   * `createObjectURL` reserva memoria hasta que se revoca a mano: sin el
   * revoke, escoger y descartar fotos treinta veces deja treinta imágenes
   * vivas en la pestaña.
   */
  const [previas, setPrevias] = useState<string[]>([])

  /**
   * LA DEPENDENCIA ES LA FIRMA, NO EL ARREGLO, Y ESTO NO ES UN DETALLE.
   *
   * `pendientes = []` construye un arreglo NUEVO en cada render. React compara
   * las dependencias por identidad, así que el efecto se volvía a disparar
   * siempre: efecto → `setPrevias` → render → otro `[]` → efecto. Un bucle sin
   * fondo, y de los que no se ven: la pantalla se pinta bien y por dentro el
   * componente se está renderizando sin parar hasta que React corta con
   * «Maximum update depth exceeded» y desmonta TODO el árbol —pantalla en
   * blanco, no responde ningún clic, el atrás no vuelve—. Se disparaba en las
   * dos pantallas que montan esto sin pasar `pendientes`: la ficha de la orden
   * y el modal de fotos del técnico.
   *
   * La firma mira el CONTENIDO —qué archivos son—, así que el mismo conjunto
   * de fotos da la misma cadena por muchos arreglos nuevos que se creen, y
   * cambia en cuanto se añade o se quita una, que es justo cuando hay que
   * rehacer las miniaturas.
   */
  const firma = pendientes.map((f) => `${f.name}:${f.size}:${f.lastModified}`).join('|')

  useEffect(() => {
    const urls = pendientes.map((f) => URL.createObjectURL(f))
    setPrevias(urls)
    return () => urls.forEach((u) => URL.revokeObjectURL(u))
    // `pendientes` queda fuera a propósito: es la firma la que dice si cambió.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firma])

  // Lo mismo para las que están en la cola: miniaturas de los blobs guardados.
  // ¿Hay conexión? Solo para el rótulo de las fotos en cola.
  const [enLinea, setEnLinea] = useState(
    typeof navigator === 'undefined' ? true : navigator.onLine,
  )
  useEffect(() => {
    const marcar = () => setEnLinea(navigator.onLine)
    window.addEventListener('online', marcar)
    window.addEventListener('offline', marcar)
    return () => {
      window.removeEventListener('online', marcar)
      window.removeEventListener('offline', marcar)
    }
  }, [])

  const [previasCola, setPreviasCola] = useState<string[]>([])
  const firmaCola = cola.enCola.map((f) => f.id).join('|')
  useEffect(() => {
    const urls = cola.enCola.map((f) => URL.createObjectURL(f.blob))
    setPreviasCola(urls)
    return () => urls.forEach((u) => URL.revokeObjectURL(u))
    // Igual que arriba: manda la firma, no el arreglo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firmaCola])

  // Adelanta la carga de Google mientras el técnico prepara la toma: así la
  // dirección del sello ya está lista cuando dispara, y no se pierde contra el
  // tope de tiempo al primer disparo sobre datos móviles.
  useEffect(() => {
    if (puedeEditar) precargarGeocodificador()
  }, [puedeEditar])

  /*
   * LA CAMARA SE ABRE SOLA, y es a proposito.
   *
   * La quite un rato —el paso entraba disparando la camara y parecia de mas—,
   * y Johan lo corrigio: «la camara si se debe abrir sola al terminar la
   * descripcion porque debe subir fotos». Tiene razon. Las fotos no son un
   * anexo opcional: sustentan la cotizacion y acaban en el acta que firma el
   * cliente. Un paso que hay que ir a buscar es un paso que en la calle no se
   * hace.
   *
   * La salida es la de la camara del sistema —su «Listo»— y detras queda este
   * paso con su cierre.
   */
  useEffect(() => {
    if (!autoAbrir || !puedeEditar || disparadoRef.current) return
    disparadoRef.current = true
    void abrirCamara()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // El visor de la foto ampliada se cierra con Escape, como cualquier diálogo.
  useEffect(() => {
    if (!ampliada) return
    const alTecla = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setAmpliada(null)
    }
    window.addEventListener('keydown', alTecla)
    return () => window.removeEventListener('keydown', alTecla)
  }, [ampliada])

  /** Le quema el sello: sin fecha, hora y sitio la foto no prueba nada. */
  async function sellar(foto: File): Promise<File> {
    const pos = await posicionParaSello()

    // La dirección legible del punto, para escribirla en vez de las coordenadas
    // en grados. Con tope de tiempo: si Google se demora, la foto no se queda
    // esperando —se estampa con las coordenadas y listo—. Nunca lanza.
    let sitioGps: string | null = null
    if (pos) {
      sitioGps = await Promise.race<string | null>([
        direccionDesdeCoords(pos.coords.latitude, pos.coords.longitude).catch(() => null),
        new Promise<null>((r) => setTimeout(() => r(null), 7000)),
      ])
    }

    return estamparFoto(foto, {
      ot: sello?.referencia ?? 'VISITA',
      direccion: sello?.direccion ?? null,
      tecnico: perfil?.nombre ?? null,
      latitud: pos?.coords.latitude ?? null,
      longitud: pos?.coords.longitude ?? null,
      exactitudM: pos?.coords.accuracy ?? null,
      sitioGps,
    })
  }

  async function recibir(foto: File) {
    setError(null)
    // EL INDICADOR TIENE QUE SALIR YA, y aquí el trabajo pesado es sellar: pedir
    // el GPS y quemar la marca en la imagen. La subida ya NO se espera —la foto
    // entra a la cola y sube sola—, así que en cuanto está sellada, listo. Sin
    // este bloqueo el técnico tocaba de nuevo creyendo que no pasó nada y se
    // abría otra cámara encima.
    setProcesando(true)
    try {
      const sellada = await sellar(foto)

      if (ordenServicioId) {
        // NO SE SUBE AQUÍ: SE GUARDA EN EL TELÉFONO Y SUBE SOLA.
        //
        // La hora de la obra ya está quemada en el sello; se guarda también
        // aparte (`tomadaEn`) para la trazabilidad, y el hash de la imagen
        // sellada para poder detectar después si alguien la alteró. Con o sin
        // señal la foto queda a salvo; el sincronizador la sube cuando pueda.
        const tomadaEn = new Date().toISOString()
        const hash = await huellaSha256(sellada)
        await cola.agregar({
          id: identificadorUnico(),
          ordenServicioId,
          blob: sellada,
          tipo: sellada.type || 'image/jpeg',
          hash,
          tomadaEn,
          creado: Date.now(),
        })
      } else {
        // Todavía no hay orden a la que colgarlas: se quedan en la mano y el
        // formulario las sube en cuanto la orden nace.
        onPendientes?.([...pendientes, sellada])
      }
    } catch (fallo) {
      setError(fallo instanceof Error ? fallo.message : 'No se pudo guardar la foto.')
    } finally {
      setProcesando(false)
    }
  }

  /** En la app, la cámara del sistema; en el navegador, la de la aplicación. */
  async function abrirCamara() {
    // Un solo disparo a la vez: si ya está tomando, sellando o subiendo una
    // foto, un segundo toque NO abre otra cámara. Era justo eso —tocar «Tomar
    // otra foto» mientras subía la anterior— lo que tumbaba la sesión.
    if (procesando || camara) return
    setError(null)
    // El GPS empieza a afinar mientras se encuadra: al disparar, el sello ya
    // tiene una ubicación buena y no la primera que salga.
    calentarUbicacion()

    if (!esNativo()) {
      setCamara(true)
      return
    }

    try {
      const foto = await fotoNativa('camara')
      if (foto) await recibir(foto)
    } catch (fallo) {
      const msg = fallo instanceof Error ? fallo.message : ''
      // Cancelar no es un error que valga la pena mostrar.
      if (!/cancel/i.test(msg)) {
        setError('No se pudo abrir la cámara. Revise que la app tenga el permiso concedido.')
      }
    }
  }

  const hay = fotos.length > 0 || pendientes.length > 0 || cola.enCola.length > 0
  // Ocupado = sellando/guardando la foto. La SUBIDA ya no bloquea: pasa en
  // segundo plano desde la cola.
  const ocupado = procesando

  return (
    <div className={estilos.zona}>
      {error && <Aviso tipo="error">{error}</Aviso>}

      {hay && (
        <ul className={estilos.rejilla}>
          {/* Las ya subidas */}
          {fotos.map((f, i) => (
            <li key={f.id} className={estilos.celda}>
              {urles[i] ? (
                <button
                  type="button"
                  className={estilos.abrir}
                  onClick={() => setAmpliada(urles[i])}
                  aria-label="Ampliar la foto"
                >
                  <img
                    className={estilos.foto}
                    src={urles[i]}
                    alt={`Foto de la visita subida por ${f.quien}`}
                    loading="lazy"
                  />
                </button>
              ) : (
                <span className={estilos.cargando} aria-hidden="true" />
              )}

              <span className={estilos.pie} title={`${f.quien} · ${fechaHora(f.creado_en)}`}>
                {f.quien}
              </span>

              {puedeEditar && (
                <button
                  type="button"
                  className={estilos.quitar}
                  onClick={() => void borrar.mutateAsync({ id: f.id, ruta: f.ruta })}
                  aria-label="Quitar esta foto"
                  title="Quitar esta foto"
                >
                  {IconoQuitar}
                </button>
              )}
            </li>
          ))}

          {/* Las que están en el teléfono esperando subir. La foto ya está
              sellada y a salvo; el rótulo dice si está subiendo o aguardando
              señal. */}
          {cola.enCola.map((f, i) => (
            <li key={f.id} className={`${estilos.celda} ${estilos.enEspera}`}>
              {previasCola[i] && (
                <button
                  type="button"
                  className={estilos.abrir}
                  onClick={() => setAmpliada(previasCola[i])}
                  aria-label="Ampliar la foto"
                >
                  <img className={estilos.foto} src={previasCola[i]} alt="Foto en el teléfono" />
                </button>
              )}
              <span className={estilos.pie}>
                {enLinea ? 'Subiendo…' : 'En el teléfono'}
              </span>

              {puedeEditar && (
                <button
                  type="button"
                  className={estilos.quitar}
                  onClick={() => void cola.quitar(f.id)}
                  aria-label="Quitar esta foto"
                >
                  {IconoQuitar}
                </button>
              )}
            </li>
          ))}

          {/* Las escogidas que todavía no existen en el servidor */}
          {pendientes.map((f, i) => (
            <li key={`${f.name}-${i}`} className={`${estilos.celda} ${estilos.enEspera}`}>
              {previas[i] && (
                <button
                  type="button"
                  className={estilos.abrir}
                  onClick={() => setAmpliada(previas[i])}
                  aria-label="Ampliar la foto"
                >
                  <img className={estilos.foto} src={previas[i]} alt={f.name} />
                </button>
              )}
              <span className={estilos.pie}>Se sube al crear</span>

              {puedeEditar && (
                <button
                  type="button"
                  className={estilos.quitar}
                  onClick={() => onPendientes?.(pendientes.filter((_, j) => j !== i))}
                  aria-label="Quitar esta foto"
                >
                  {IconoQuitar}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {puedeEditar && (
        <>
          <button
            type="button"
            // Las fotos sí salen en papel; el botón de tomar otra, no.
            className={`${estilos.anadir} no-imprimir`}
            onClick={() => void abrirCamara()}
            disabled={ocupado}
          >
            {ocupado ? (
              <>
                <span className={estilos.rueda} aria-hidden="true" /> Subiendo…
              </>
            ) : hay ? (
              'Tomar otra foto'
            ) : (
              'Tomar fotos de la visita'
            )}
          </button>

          <CamaraEnVivo
            abierto={camara}
            onCerrar={() => setCamara(false)}
            onTomada={(foto) => recibir(foto)}
          />

          {!hay && (
            <p className={estilos.ayuda}>
              Lo que se vio antes de tocar nada: el tablero, la acometida, lo que haya que
              arreglar. Se toman aquí mismo —no se pueden traer de la galería— y quedan con la
              fecha, la hora y el sitio quemados encima.
            </p>
          )}
        </>
      )}

      {ampliada && (
        <div
          className={estilos.visor}
          role="dialog"
          aria-modal="true"
          onClick={() => setAmpliada(null)}
        >
          <img className={estilos.visorFoto} src={ampliada} alt="Foto ampliada" />
          <button
            type="button"
            className={estilos.visorCerrar}
            aria-label="Cerrar"
            onClick={() => setAmpliada(null)}
          >
            ×
          </button>
        </div>
      )}
    </div>
  )
}
